import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { useToast } from '../../context/ToastContext.jsx';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import StatusBadge from '../StatusBadge.jsx';
import Modal from '../Modal.jsx';

const POLICY_TYPES = ['preserve_and_append', 'biddigi_only'];

function ownerLabel(row) {
  if (row.publisher_id) return { kind: 'Publisher', name: row.publisher_name || `#${row.publisher_id}` };
  return { kind: 'Inbound seat', name: row.seat_name || `#${row.inbound_seat_id}` };
}

export default function SchainTab() {
  const toast = useToast();
  const [policies, setPolicies] = useState(null);
  const [publishers, setPublishers] = useState([]);
  const [seats, setSeats] = useState([]);
  const [demandPartners, setDemandPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [pol, pubs, seatList, partners] = await Promise.all([
        api.listSchainPolicies(),
        api.listPublishers().catch(() => []),
        api.listInboundSeats().catch(() => []),
        api.listDemandPartners().catch(() => []),
      ]);
      setPolicies(pol);
      setPublishers(pubs);
      setSeats(seatList);
      setDemandPartners(partners);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load schain policies.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function deletePolicy(row) {
    const owner = ownerLabel(row);
    if (!window.confirm(`Delete the schain policy for ${owner.kind.toLowerCase()} "${owner.name}"? Auctions for this owner will fall back to plain preserve_and_append with no known upstream.`)) return;
    try {
      await api.deleteSchainPolicy(row.id);
      toast.success('Schain policy deleted.');
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete policy.');
    }
  }

  return (
    <div className="stack">
      <SchainPreviewCard publishers={publishers} seats={seats} demandPartners={demandPartners} />

      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Schain policies</h2>
          <button className="btn btn--primary btn--sm" onClick={() => setCreating(true)}>
            + Add policy
          </button>
        </div>
        <p className="card__hint">
          Default (no row here) is <code>preserve_and_append</code> with no known upstream seller --
          the always-honest fallback. A policy is only needed to declare a known upstream seller
          identity, or to opt into <code>biddigi_only</code> for a genuinely unknown upstream.
        </p>

        {loading && <LoadingState label="Loading schain policies" />}
        {!loading && error && <EmptyState title="Couldn't load schain policies" message={error} />}
        {!loading && !error && policies && policies.length === 0 && (
          <EmptyState title="No schain policies configured" message="Every owner currently falls back to plain preserve_and_append with no known upstream." />
        )}
        {!loading && !error && policies && policies.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Owner</th>
                  <th>Policy type</th>
                  <th>Upstream seller</th>
                  <th>Upstream hp</th>
                  <th>Complete override</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {policies.map((row) => {
                  const owner = ownerLabel(row);
                  return (
                    <tr key={row.id}>
                      <td className="cell-title">
                        {owner.name}
                        <div className="cell-subtitle">{owner.kind}</div>
                      </td>
                      <td>
                        <code className="code-chip">{row.policy_type}</code>
                      </td>
                      <td className="cell-muted">
                        {row.upstream_seller_domain || row.upstream_seller_id
                          ? `${row.upstream_seller_domain || '—'} / ${row.upstream_seller_id || '—'}`
                          : '—'}
                      </td>
                      <td className="cell-muted">{row.upstream_hp ?? '—'}</td>
                      <td className="cell-muted">{row.complete_override ?? '—'}</td>
                      <td>
                        <button className="btn btn--ghost btn--sm" onClick={() => setEditing(row)}>Edit</button>{' '}
                        <button className="btn btn--ghost btn--sm" onClick={() => deletePolicy(row)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {creating && (
        <PolicyModal
          publishers={publishers}
          seats={seats}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {editing && (
        <PolicyModal
          publishers={publishers}
          seats={seats}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function PolicyModal({ publishers, seats, existing, onClose, onSaved }) {
  const toast = useToast();
  const isEdit = !!existing;
  const [ownerType, setOwnerType] = useState(existing ? (existing.publisher_id ? 'publisher' : 'seat') : 'publisher');
  const [ownerId, setOwnerId] = useState(existing ? String(existing.publisher_id || existing.inbound_seat_id) : '');
  const [policyType, setPolicyType] = useState(existing?.policy_type || 'preserve_and_append');
  const [upstreamDomain, setUpstreamDomain] = useState(existing?.upstream_seller_domain || '');
  const [upstreamSellerId, setUpstreamSellerId] = useState(existing?.upstream_seller_id || '');
  const [upstreamHp, setUpstreamHp] = useState(existing?.upstream_hp === 0 || existing?.upstream_hp === 1 ? String(existing.upstream_hp) : '');
  const [completeOverride, setCompleteOverride] = useState(existing?.complete_override === 0 || existing?.complete_override === 1 ? String(existing.complete_override) : '');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    if (!isEdit && !ownerId) {
      setFormError(`Select a ${ownerType === 'publisher' ? 'publisher' : 'inbound seat'}.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        policy_type: policyType,
        upstream_seller_domain: upstreamDomain.trim() || null,
        upstream_seller_id: upstreamSellerId.trim() || null,
        upstream_hp: upstreamHp === '' ? null : Number(upstreamHp),
        complete_override: completeOverride === '' ? null : Number(completeOverride),
      };
      if (isEdit) {
        await api.patchSchainPolicy(existing.id, payload);
        toast.success('Schain policy updated.');
      } else {
        await api.createSchainPolicy({
          ...payload,
          publisher_id: ownerType === 'publisher' ? Number(ownerId) : null,
          inbound_seat_id: ownerType === 'seat' ? Number(ownerId) : null,
        });
        toast.success('Schain policy created.');
      }
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not save schain policy.';
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={isEdit ? 'Edit schain policy' : 'Add schain policy'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        {!isEdit && (
          <>
            <div className="field">
              <span className="field__label">Owner</span>
              <div className="checkbox-row">
                <label className="field field--checkbox field--checkbox-inline">
                  <input type="radio" checked={ownerType === 'publisher'} onChange={() => { setOwnerType('publisher'); setOwnerId(''); }} />
                  <span>Publisher</span>
                </label>
                <label className="field field--checkbox field--checkbox-inline">
                  <input type="radio" checked={ownerType === 'seat'} onChange={() => { setOwnerType('seat'); setOwnerId(''); }} />
                  <span>Inbound seat</span>
                </label>
              </div>
            </div>
            <label className="field">
              <span className="field__label">{ownerType === 'publisher' ? 'Publisher' : 'Inbound seat'}</span>
              <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                <option value="">Select&hellip;</option>
                {(ownerType === 'publisher' ? publishers : seats).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}{ownerType === 'publisher' ? ` (${o.domain})` : ` (${o.seatId})`}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
        <label className="field">
          <span className="field__label">Policy type</span>
          <select value={policyType} onChange={(e) => setPolicyType(e.target.value)}>
            {POLICY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <p className="card__hint">
          {policyType === 'biddigi_only'
            ? 'An explicit, auditable exception for when there is genuinely no known/verified upstream seller identity -- never a way to hide a known real chain.'
            : 'Preserves any real incoming schain verbatim, appending BidDigi\'s own node. The upstream fields below only apply when no real incoming schain is present.'}
        </p>
        <label className="field">
          <span className="field__label">Upstream seller domain <span className="field__optional">(asi)</span></span>
          <input value={upstreamDomain} onChange={(e) => setUpstreamDomain(e.target.value)} placeholder="e.g. upstream-partner.com" />
        </label>
        <label className="field">
          <span className="field__label">Upstream seller ID <span className="field__optional">(sid)</span></span>
          <input value={upstreamSellerId} onChange={(e) => setUpstreamSellerId(e.target.value)} placeholder="e.g. 12345" />
        </label>
        <label className="field">
          <span className="field__label">Upstream hp <span className="field__optional">(optional)</span></span>
          <select value={upstreamHp} onChange={(e) => setUpstreamHp(e.target.value)}>
            <option value="">Not set</option>
            <option value="0">0</option>
            <option value="1">1</option>
          </select>
        </label>
        <label className="field">
          <span className="field__label">Complete override <span className="field__optional">(optional)</span></span>
          <select value={completeOverride} onChange={(e) => setCompleteOverride(e.target.value)}>
            <option value="">Not set (never auto-claims complete)</option>
            <option value="0">0 -- explicitly incomplete</option>
            <option value="1">1 -- attest full knowledge of the chain</option>
          </select>
        </label>
        {formError && <div className="form-error">{formError}</div>}
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
}

// "Preview Outbound schain" -- the mandate's explicitly-named critical feature. Calls the exact
// same buildOutboundSchain() function the real auction path uses (see the backend route's own
// header), so this shows the REAL resulting schain, not an approximation.
function SchainPreviewCard({ publishers, seats, demandPartners }) {
  const toast = useToast();
  const [ownerType, setOwnerType] = useState('publisher');
  const [ownerId, setOwnerId] = useState('');
  const [demandPartnerCode, setDemandPartnerCode] = useState('');
  const [incomingSchainText, setIncomingSchainText] = useState('');
  const [running, setRunning] = useState(false);
  const [formError, setFormError] = useState(null);
  const [result, setResult] = useState(null);

  async function runPreview(e) {
    e.preventDefault();
    setFormError(null);
    setResult(null);
    if (!ownerId) {
      setFormError(`Select a ${ownerType === 'publisher' ? 'publisher' : 'inbound seat'}.`);
      return;
    }
    let incomingSchain = null;
    if (incomingSchainText.trim()) {
      try {
        incomingSchain = JSON.parse(incomingSchainText);
      } catch {
        setFormError('Simulated incoming schain is not valid JSON.');
        return;
      }
    }
    setRunning(true);
    try {
      const body = {
        publisherId: ownerType === 'publisher' ? Number(ownerId) : undefined,
        inboundSeatId: ownerType === 'seat' ? Number(ownerId) : undefined,
        demandPartnerCode: demandPartnerCode || undefined,
        incomingSchain,
      };
      const res = await api.previewSchain(body);
      setResult(res);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not run schain preview.';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  }

  return (
    <section className="card">
      <div className="card__header">
        <h2 className="card__title">Preview Outbound schain</h2>
      </div>
      <p className="card__hint">
        Shows EXACTLY the schain the real auction path would send for this owner -- the same
        algorithm, not an approximation. Optionally simulate a real incoming schain (as an app
        publisher or ad server would send it) to see how it gets preserved and appended to.
      </p>
      <form onSubmit={runPreview} className="form">
        <div className="field">
          <span className="field__label">Supply owner</span>
          <div className="checkbox-row">
            <label className="field field--checkbox field--checkbox-inline">
              <input type="radio" checked={ownerType === 'publisher'} onChange={() => { setOwnerType('publisher'); setOwnerId(''); }} />
              <span>Publisher</span>
            </label>
            <label className="field field--checkbox field--checkbox-inline">
              <input type="radio" checked={ownerType === 'seat'} onChange={() => { setOwnerType('seat'); setOwnerId(''); }} />
              <span>Inbound seat</span>
            </label>
          </div>
        </div>
        <label className="field">
          <span className="field__label">{ownerType === 'publisher' ? 'Publisher' : 'Inbound seat'}</span>
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            <option value="">Select&hellip;</option>
            {(ownerType === 'publisher' ? publishers : seats).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}{ownerType === 'publisher' ? ` (${o.domain})` : ` (${o.seatId})`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Demand partner <span className="field__optional">(optional -- for display/context only)</span></span>
          <select value={demandPartnerCode} onChange={(e) => setDemandPartnerCode(e.target.value)}>
            <option value="">None</option>
            {demandPartners.map((p) => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Simulated incoming schain <span className="field__optional">(optional JSON)</span></span>
          <textarea
            rows={5}
            value={incomingSchainText}
            onChange={(e) => setIncomingSchainText(e.target.value)}
            placeholder={'{\n  "ver": "1.0",\n  "complete": 1,\n  "nodes": [{ "asi": "upstream.com", "sid": "111", "hp": 1 }]\n}'}
            style={{ fontFamily: 'var(--font-mono)', fontSize: '12.5px' }}
          />
        </label>
        {formError && <div className="form-error">{formError}</div>}
        <div className="button-row">
          <button type="submit" className="btn btn--primary" disabled={running}>{running ? 'Running…' : 'Preview outbound schain'}</button>
        </div>
      </form>

      {result && <SchainPreviewResult result={result} />}
    </section>
  );
}

function SchainPreviewResult({ result }) {
  return (
    <div className="card" style={{ marginTop: '1rem', background: 'var(--surface-sunken)' }}>
      <div className="card__header">
        <h3 className="card__title">Result</h3>
        <StatusBadge status={result.valid ? 'active' : 'error'} />
      </div>
      <p className="card__hint">
        Policy resolved from: <strong>{result.context.policyResolvedFrom}</strong> &middot; BidDigi's own node:{' '}
        <code className="code-chip">{result.context.ownNode.asi} / {result.context.ownNode.sid}</code>
        {result.context.sellerAccountUsed && <> &middot; Seller account used: <code className="code-chip">{result.context.sellerAccountUsed}</code></>}
        {result.context.simulatedIncomingSchain && <> &middot; Using a simulated incoming schain</>}
      </p>
      {result.context.note && <p className="card__hint">{result.context.note}</p>}

      <pre className="diff-view">{JSON.stringify(result.schain, null, 2)}</pre>

      {result.errors && result.errors.length > 0 && (
        <div className="form-error">
          <strong>Errors:</strong>
          <ul className="plain-list">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}
      {result.warnings && result.warnings.length > 0 && (
        <div className="card__hint card__hint--warning">
          <strong>Warnings:</strong>
          <ul className="plain-list">{result.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}
    </div>
  );
}
